import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { ChevronRight, FileText, Library, Search, Settings, Users } from "@/components/icons"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useWebSearchEnabled } from "@/hooks/use-route-overrides"
import { cn } from "@/lib/utils"
import { type Project } from "@/services/project-service"

import { ProjectFileSettings } from "./project-file-settings"
import { ProjectGeneralSettings } from "./project-general-settings"
import { ProjectLibrariesSettings } from "./project-libraries-settings"
import { ProjectSharingSettings } from "./project-sharing-settings"
import { ProjectWebSearchSettings } from "./project-websearch-settings"

const useSettingsSections = (showWebSearchSection: boolean) => {
  const { t } = useTranslation("", { keyPrefix: "project_settings" })

  return [
    {
      id: "general" as const,
      label: t("general.title"),
      icon: Settings,
      description: t("general.description"),
    },
    {
      id: "sharing" as const,
      label: t("sharing.title", { defaultValue: "Sharing" }),
      icon: Users,
      description: t("sharing.description", { defaultValue: "Manage who can access this project" }),
    },
    {
      id: "files" as const,
      label: t("files.title"),
      icon: FileText,
      description: t("files.description"),
    },
    {
      id: "libraries" as const,
      label: t("libraries.title"),
      icon: Library,
      description: t("libraries.description"),
    },
    ...(showWebSearchSection
      ? [
          {
            id: "web-search" as const,
            label: t("web_search.title"),
            icon: Search,
            description: t("web_search.description"),
          },
        ]
      : []),
  ]
}

export type SettingsSection = "general" | "sharing" | "files" | "libraries" | "web-search"

type ProjectSettingsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  project: Project | undefined
  sessionType: string
  defaultSection?: SettingsSection
}

export function ProjectSettingsDialog({
  open,
  onOpenChange,
  projectId,
  project,
  sessionType,
  defaultSection = "general",
}: ProjectSettingsDialogProps) {
  const [activeSection, setActiveSection] = useState<SettingsSection>(defaultSection)
  const { t } = useTranslation("", { keyPrefix: "project_settings" })
  const showWebSearchSection = useWebSearchEnabled()
  const settingsSections = useSettingsSections(showWebSearchSection)

  useEffect(() => {
    if (open) {
      setActiveSection(
        defaultSection === "web-search" && !showWebSearchSection ? "general" : defaultSection
      )
    }
  }, [defaultSection, open, showWebSearchSection])

  useEffect(() => {
    if (!showWebSearchSection && activeSection === "web-search") {
      setActiveSection("general")
    }
  }, [activeSection, showWebSearchSection])

  // Compute permissions based on user's role
  const canEdit =
    project?.currentUserRole === "owner" ||
    project?.currentUserRole === "admin" ||
    project?.currentUserRole === "editor"

  const renderSectionContent = () => {
    switch (activeSection) {
      case "general":
        return (
          <ProjectGeneralSettings
            canEdit={canEdit}
            project={project}
            projectId={projectId}
            sessionType={sessionType}
          />
        )

      case "sharing":
        return (
          <ProjectSharingSettings
            project={project}
            projectId={projectId}
            sessionType={sessionType}
          />
        )

      case "files":
        return <ProjectFileSettings canEdit={canEdit} projectId={projectId} />

      case "libraries":
        return (
          <ProjectLibrariesSettings
            canEdit={canEdit}
            project={project}
            projectId={projectId}
            sessionType={sessionType}
          />
        )

      case "web-search":
        return (
          <ProjectWebSearchSettings
            canEdit={canEdit}
            project={project}
            projectId={projectId}
            sessionType={sessionType}
          />
        )

      default:
        return null
    }
  }

  if (!project) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-full overflow-hidden p-0 sm:max-w-4xl md:max-h-[95vh] lg:max-h-[700px]">
        <div className="flex h-full flex-col lg:grid lg:grid-cols-[280px_1fr]">
          {/* Header - Mobile Only */}
          <div className="flex-shrink-0 border-b px-4 py-4 lg:hidden">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">{t("dialog_title")}</DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                {t("dialog_description")}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Mobile Tab Navigation */}
          <div className="bg-muted/30 flex-shrink-0 border-b lg:hidden">
            <nav className="flex flex-wrap">
              {settingsSections.map((section) => {
                const Icon = section.icon
                const isActive = activeSection === section.id

                return (
                  <button
                    key={section.id}
                    className={cn(
                      "flex flex-1 items-center justify-center gap-2 border-b-2 px-3 py-3 text-sm transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      "min-w-0 basis-1/2", // Each tab takes half the width, allowing 2 per row
                      isActive
                        ? "border-primary bg-accent text-accent-foreground font-medium"
                        : "border-transparent"
                    )}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate text-xs sm:text-sm">{section.label}</span>
                  </button>
                )
              })}
            </nav>
          </div>

          {/* Desktop Sidebar Navigation */}
          <div className="bg-muted/30 hidden border-r p-6 lg:block">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-lg font-semibold">{t("dialog_title")}</DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                {t("dialog_description")}
              </DialogDescription>
            </DialogHeader>

            <nav className="space-y-1">
              {settingsSections.map((section) => {
                const Icon = section.icon
                const isActive = activeSection === section.id

                return (
                  <button
                    key={section.id}
                    className={cn(
                      "group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      isActive && "bg-accent text-accent-foreground font-medium"
                    )}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{section.label}</div>
                      <div
                        className={cn(
                          "text-muted-foreground group-hover:text-accent-foreground truncate text-xs",
                          isActive && "text-accent-foreground"
                        )}
                      >
                        {section.description}
                      </div>
                    </div>
                    {isActive && <ChevronRight className="h-4 w-4 shrink-0" />}
                  </button>
                )
              })}
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex flex-1 flex-col overflow-hidden">
            {/* Desktop Header */}
            <div className="hidden flex-shrink-0 border-b px-6 py-4 lg:block">
              <h3 className="text-lg font-semibold">
                {settingsSections.find((s) => s.id === activeSection)?.label}
              </h3>
              <p className="text-muted-foreground mt-1 text-sm">
                {settingsSections.find((s) => s.id === activeSection)?.description}
              </p>
            </div>

            {/* Mobile Header */}
            <div className="flex-shrink-0 border-b px-4 py-3 lg:hidden">
              <h3 className="font-semibold">
                {settingsSections.find((s) => s.id === activeSection)?.label}
              </h3>
              <p className="text-muted-foreground mt-1 text-xs">
                {settingsSections.find((s) => s.id === activeSection)?.description}
              </p>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6">
              <div className="mx-auto max-w-2xl">{renderSectionContent()}</div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
